// OWNED BY the tasks tab agent (created under detail/tabs/tasks/).
//
// Business logic for the customer-page tasks tab, PORTED (logic only — no
// component reuse) from the legacy calendar tasks surfaces:
//   - overdue detection / grouping: src/utils/calendar/filters.ts (getFilteredEventsByType)
//   - postpone-to-today + bulk move: CalendarTasks.tsx / CalendarTasksRow.tsx
//   - owner-only guard: CalendarTasksRow.tsx (isOwner = user.email === event.userId)
//   - per-contact filtering: ContactTableNestedRowEvents.tsx (attendees[].phoneNumberInternational)
//   - task write shape (colors/allDay/start/end/status): CalendarForm.tsx
//
// Writes intentionally follow the app's calendar write path: Firestore write
// via CalendarContext.createOrUpdateEvent (which then fires the
// POST /api/v1/webot-calendar/*-event hook — the backend skips WhatsApp for
// eventType 'task', so quick-add never messages the customer). Status toggles
// and postpones are raw Firestore updates, exactly like the legacy rows
// (no backend hook — matches CalendarPage/CalendarTasksRow behavior).

import { db, firebase } from "firebaseConfig";
import { addDays, addHours, differenceInDays, isSameWeek, set, startOfDay, format } from "date-fns";
import { GenericConverter } from "utils/firestoreConverters";
import { getFullName } from "utils/Haim/utils";
import { getTaskStartEndDate, parseContactsToAttendees } from "utils/calendar/helper";
import { fetchCached, getCached, setCached } from "../../data/sessionCache";
import {
  CALENDAR_EVENTS_SUBCOLLECTION_NAME,
  CALENDAR_SETTINGS_COLLECTION_NAME
} from "../../../../../../@types/sharedSchema";
import type { CalendarEvent, RealEstateContact } from "../../../../../../@types/sharedSchema";

// ---------------------------------------------------------------------------
// Session-cache warm-up (fired from preloadTasksTab on contacts-row hover).
// The whole own calendar is cached ONCE per user (not per contact) — hovering
// many rows dedups to a single fetch through fetchCached.
// ---------------------------------------------------------------------------

export const contactTasksCacheKey = (userEmail: string): string =>
  `tasks:calendar-events:${userEmail}`;

const fetchOwnCalendarEvents = async (userEmail: string): Promise<CalendarEvent[]> => {
  const snapshot = await db
    .collection(CALENDAR_SETTINGS_COLLECTION_NAME)
    .doc(userEmail)
    .collection(CALENDAR_EVENTS_SUBCOLLECTION_NAME)
    .withConverter(GenericConverter<CalendarEvent>())
    .get();

  return snapshot.docs.filter((doc) => doc.id != null).map((doc) => doc.data());
};

/** One-shot warm of the user's own calendar into the session cache. Error-safe. */
export function warmContactTasksCache(): void {
  const userEmail = firebase.auth().currentUser?.email;
  if (!userEmail) return;

  // Once ANY value exists for the key — a previous warm fetch, or the live
  // CalendarContext mirror in useContactTasks — skip entirely. Re-downloading
  // the whole own-calendar subcollection on hover churn is pure waste: the
  // live listener (started by the tasks tab / calendar surfaces) is the
  // freshness source, and a session-old snapshot is fine as a cold-start
  // placeholder until it takes over.
  if (getCached<CalendarEvent[]>(contactTasksCacheKey(userEmail)) !== undefined) return;

  void fetchCached(contactTasksCacheKey(userEmail), () => fetchOwnCalendarEvents(userEmail), {
    maxAgeMs: 10 * 60_000
  }).catch(() => undefined);
}

/**
 * Optimistic local patch of the cached own-calendar snapshot (notifies cache
 * subscribers → re-renders the tab/badge). Used by the cold window ONLY —
 * before CalendarContext's live listener has emitted, when Firestore latency
 * compensation has no listener to feed. No-op when nothing is cached.
 */
export function patchCachedOwnCalendarEvents(
  updater: (events: CalendarEvent[]) => CalendarEvent[]
): void {
  const userEmail = firebase.auth().currentUser?.email;
  if (!userEmail) return;

  const key = contactTasksCacheKey(userEmail);
  const cached = getCached<CalendarEvent[]>(key);
  if (cached) setCached(key, updater(cached));
}

// ---------------------------------------------------------------------------
// Per-contact filtering (ported from ContactTableNestedRowEvents.getContactEvents,
// hardened: matches attendee phones against BOTH contact.id (E.164 — what
// parseContactsToAttendees stores) and callerPhoneNumber, plus the
// Cloud-Function-stamped contactId field).
// ---------------------------------------------------------------------------

export function isEventOfContact(event: CalendarEvent, contact: RealEstateContact): boolean {
  const contactPhones = new Set(
    [contact.id, contact.contactId].filter(Boolean) as string[]
  );

  if (event.contactId && contactPhones.has(event.contactId)) return true;

  return (event.attendees ?? []).some(
    ({ phoneNumberInternational }) =>
      !!phoneNumberInternational && contactPhones.has(phoneNumberInternational)
  );
}

// ---------------------------------------------------------------------------
// Owner guard (ported from CalendarTasksRow.isOwner, tolerant of the
// Cloud-Function stamping latency: an event in the OWN calendar without a
// userId stamp yet is treated as own).
// ---------------------------------------------------------------------------

export function isOwnCalendarEvent(event: CalendarEvent, userEmail?: string | null): boolean {
  return !event.userId || event.userId === userEmail;
}

/** Doc ref for updates — prefers event.path (works for shared-calendar events). */
export function eventDocRef(
  event: CalendarEvent,
  userEmail: string
): firebase.firestore.DocumentReference {
  if (event.path) return db.doc(event.path);

  return db
    .collection(CALENDAR_SETTINGS_COLLECTION_NAME)
    .doc(userEmail)
    .collection(CALENDAR_EVENTS_SUBCOLLECTION_NAME)
    .doc(event.id);
}

// ---------------------------------------------------------------------------
// Grouping — mock s20 groups (disjoint, tasks only). Ported from
// getFilteredEventsByType with two deliberate adjustments:
//   - groups are DISJOINT (a done task appears only under "הושלמו";
//     legacy tabs showed done tasks inside date tabs too);
//   - overdue matches status !== 'done' (legacy required === 'pending';
//     tasks created outside CalendarForm may miss the status field and must
//     not silently disappear).
// The done group keeps the legacy ~90-day horizon (implemented as "due date
// within the last 90 days" — the legacy comparison direction was buggy).
// ---------------------------------------------------------------------------

export type ContactTaskGroupId = "overdue" | "today" | "thisWeek" | "upcoming" | "done";

export const TASK_GROUP_ORDER: readonly ContactTaskGroupId[] = [
  "overdue",
  "today",
  "thisWeek",
  "upcoming",
  "done"
];

export const TASK_GROUP_LABELS: Record<ContactTaskGroupId, string> = {
  overdue: "באיחור",
  today: "היום",
  thisWeek: "השבוע",
  upcoming: "בהמשך",
  done: "הושלמו"
};

export type ContactTaskGroups = Record<ContactTaskGroupId, CalendarEvent[]>;

export function groupContactTasks(tasks: CalendarEvent[]): ContactTaskGroups {
  const today = startOfDay(new Date());

  const groups: ContactTaskGroups = {
    overdue: [],
    today: [],
    thisWeek: [],
    upcoming: [],
    done: []
  };

  for (const task of tasks) {
    const taskDay = startOfDay(new Date(task.start));
    const dayDiff = differenceInDays(taskDay, today);

    if (task.status === "done") {
      if (dayDiff >= -90) groups.done.push(task);
      continue;
    }

    // Ported overdue detection: pending && start day before today.
    if (dayDiff < 0) groups.overdue.push(task);
    else if (dayDiff === 0) groups.today.push(task);
    else if (isSameWeek(taskDay, today)) groups.thisWeek.push(task);
    else groups.upcoming.push(task);
  }

  const byStartAsc = (a: CalendarEvent, b: CalendarEvent): number =>
    new Date(a.start).getTime() - new Date(b.start).getTime();

  groups.overdue.sort(byStartAsc);
  groups.today.sort(byStartAsc);
  groups.thisWeek.sort(byStartAsc);
  groups.upcoming.sort(byStartAsc);
  groups.done.sort((a, b) => byStartAsc(b, a)); // most recent completions first

  return groups;
}

// ---------------------------------------------------------------------------
// Due-date captions (mock: "אתמול · באיחור יום", "היום 14:00", "חמישי 16.7")
// ---------------------------------------------------------------------------

const HEBREW_DAY_NAMES = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];

export function formatTaskDueLabel(task: CalendarEvent, group: ContactTaskGroupId): string {
  const start = new Date(task.start);
  const timePart = task.allDay ? "" : ` ${format(start, "HH:mm")}`;

  switch (group) {
    case "overdue": {
      const daysLate = differenceInDays(startOfDay(new Date()), startOfDay(start));
      const when = daysLate === 1 ? "אתמול" : format(start, "d.M");
      const late = daysLate === 1 ? "באיחור יום" : `באיחור ${daysLate} ימים`;
      return `${when}${timePart} · ${late}`;
    }
    case "today":
      return `היום${timePart}`;
    case "thisWeek":
      return `${HEBREW_DAY_NAMES[start.getDay()]} ${format(start, "d.M")}${timePart}`;
    case "upcoming":
      return `${format(start, "d.M.yyyy")}${timePart}`;
    case "done":
      return `הושלמה · ${format(start, "d.M")}`;
    default:
      return "";
  }
}

// ---------------------------------------------------------------------------
// Task builders (write shape ported from CalendarForm's onSubmit for tasks:
// backgroundColor === textColor, status 'pending', all-day = startOfDay→+1d,
// timed = 1-hour window, attendees via parseContactsToAttendees).
// ---------------------------------------------------------------------------

const DEFAULT_TASK_COLOR = "#1890FF"; // CalendarForm's default event color

type TaskTiming = { start: Date; end: Date; allDay: boolean };

export function buildContactTask(
  contact: RealEstateContact,
  { title, description = "", start, end, allDay }: TaskTiming & {
    title: string;
    /** Overflow of a long voice transcription (see splitTranscriptionIntoTask). */
    description?: string;
  }
): CalendarEvent {
  return {
    title,
    description,
    textColor: DEFAULT_TASK_COLOR,
    backgroundColor: DEFAULT_TASK_COLOR,
    allDay,
    start,
    end,
    eventType: "task",
    status: "pending",
    attendees: parseContactsToAttendees([contact]),
    listingIds: [],
    listingsData: []
  };
}

/** One-tap presets. All-day tasks — a preset expresses a day, not a moment. */
export type QuickAddDuePreset = "today" | "tomorrow" | "nextWeek";

/** Presets + "custom" (an exact date AND time chosen in the picker dialog). */
export type QuickAddDueOption = QuickAddDuePreset | "custom";

export const QUICK_ADD_DUE_LABELS: Record<QuickAddDuePreset, string> = {
  today: "היום",
  tomorrow: "מחר",
  nextWeek: "בעוד שבוע"
};

export const QUICK_ADD_DUE_OPTIONS: readonly QuickAddDuePreset[] = [
  "today",
  "tomorrow",
  "nextWeek"
];

/** Menu entry that opens the date+time picker. */
export const CUSTOM_DUE_MENU_LABEL = "בחירת תאריך ושעה…";

/**
 * Timing for the selected due option.
 *
 * Presets stay ALL-DAY (getTaskStartEndDate → startOfDay…next startOfDay), the
 * shape CalendarForm writes for a dateless task. A custom pick carries a real
 * clock time, so it is written TIMED with a 1-hour window — identical to the
 * "📞 להתקשר מחר ב-10:00" quick chip below, which is the app's existing shape
 * for a task due at a specific time. formatTaskDueLabel already appends HH:mm
 * for any task with allDay false, so the chosen time shows up on the row.
 */
export function dueOptionTiming(option: QuickAddDueOption, customAt?: Date | null): TaskTiming {
  if (option === "custom") {
    // Defensive: "custom" with nothing picked can only happen if a caller sets
    // the option without a date — fall back to today rather than writing an
    // Invalid Date into Firestore.
    if (!customAt || Number.isNaN(customAt.getTime())) return dueOptionTiming("today");

    const start = set(customAt, { seconds: 0, milliseconds: 0 });
    return { start, end: addHours(start, 1), allDay: false };
  }

  const daysAhead = option === "today" ? 0 : option === "tomorrow" ? 1 : 7;
  const { start, end } = getTaskStartEndDate(addDays(new Date(), daysAhead));
  return { start, end, allDay: true };
}

/** Caption for the due chip — reflects the exact moment once one is picked. */
export function formatDueChipLabel(option: QuickAddDueOption, customAt?: Date | null): string {
  if (option !== "custom") return QUICK_ADD_DUE_LABELS[option];
  if (!customAt || Number.isNaN(customAt.getTime())) return CUSTOM_DUE_MENU_LABEL;

  const sameYear = customAt.getFullYear() === new Date().getFullYear();
  return format(customAt, sameYear ? "d.M · HH:mm" : "d.M.yy · HH:mm");
}

/** Starting value for the picker: the next round hour (14:37 → 15:00). */
export function defaultCustomDueDate(): Date {
  return set(addHours(new Date(), 1), { minutes: 0, seconds: 0, milliseconds: 0 });
}

// ---------------------------------------------------------------------------
// Voice quick-add: transcription → task fields.
// ---------------------------------------------------------------------------

// A spoken task is normally one short line ("להתקשר לדנה מחר בבוקר"), but the
// recorder allows up to 2 minutes of speech — far more than belongs in a title
// (task rows render the title noWrap). CalendarEvent has a `description`, so a
// long transcription is SPLIT rather than truncated: a readable title plus the
// remainder kept verbatim in the description, which the calendar event editor
// shows. Nothing the customer dictated is ever discarded.
const TASK_TITLE_MAX_CHARS = 80;

export function splitTranscriptionIntoTask(transcription: string): {
  title: string;
  description: string;
} {
  const text = transcription.trim();
  if (!text) return { title: "", description: "" };

  // Common case: a short single-line transcription simply IS the title.
  if (text.length <= TASK_TITLE_MAX_CHARS && !/[\r\n]/.test(text)) {
    return { title: text, description: "" };
  }

  // Otherwise cut at the best break inside the limit, preferring the end of the
  // first sentence, then a line break, then the last whole word. A hard cut is
  // the last resort (one unbroken 80-character word).
  const head = text.slice(0, TASK_TITLE_MAX_CHARS);
  const sentenceMatch = /[.!?](\s|$)/.exec(head);
  const lineBreakIndex = head.search(/[\r\n]/);
  const lastSpaceIndex = head.lastIndexOf(" ");

  let cut = TASK_TITLE_MAX_CHARS;
  let cutAtSentenceEnd = false;

  if (sentenceMatch) {
    cut = sentenceMatch.index + 1;
    cutAtSentenceEnd = true;
  } else if (lineBreakIndex > 0) {
    cut = lineBreakIndex;
  } else if (lastSpaceIndex > 0) {
    cut = lastSpaceIndex;
  }

  const title = text.slice(0, cut).trim().replace(/[\s.,;:!?]+$/, "");
  const description = text.slice(cut).trim();

  return {
    // The ellipsis signals a mid-sentence cut; a title that already ends a
    // sentence reads as complete and doesn't need one.
    title: description && !cutAtSentenceEnd ? `${title}…` : title,
    description
  };
}

export type QuickAddChip = {
  key: string;
  label: string;
  build: () => CalendarEvent;
};

export function buildQuickAddChips(contact: RealEstateContact): QuickAddChip[] {
  const name = getFullName(contact.callerFirstName, contact.callerLastName);

  return [
    {
      key: "call",
      label: "📞 להתקשר מחר ב-10:00",
      build: () => {
        const start = set(addDays(new Date(), 1), {
          hours: 10,
          minutes: 0,
          seconds: 0,
          milliseconds: 0
        });
        return buildContactTask(contact, {
          title: name ? `להתקשר ל${name}` : "להתקשר ללקוח",
          start,
          end: addHours(start, 1),
          allDay: false
        });
      }
    },
    {
      key: "tour",
      label: "🏠 לקבוע סיור",
      build: () =>
        buildContactTask(contact, {
          title: name ? `לקבוע סיור עם ${name}` : "לקבוע סיור",
          ...dueOptionTiming("today")
        })
    },
    {
      key: "docs",
      label: "📄 לשלוח מסמכים",
      build: () =>
        buildContactTask(contact, {
          title: name ? `לשלוח מסמכים ל${name}` : "לשלוח מסמכים",
          ...dueOptionTiming("today")
        })
    },
    {
      key: "followUp",
      label: "⏰ מעקב בעוד שבוע",
      build: () =>
        buildContactTask(contact, {
          title: name ? `מעקב — ${name}` : "מעקב",
          ...dueOptionTiming("nextWeek")
        })
    }
  ];
}
